/**
 * Slot utility functions for time slot management
 */

/**
 * Calculate slot time from slot index within a period
 * Each period has its own slot indices starting from 0
 * Morning: 6 AM - 12 PM (0-23 = 24 slots = 6 hours * 4 slots/hour)
 * Noon: 12 PM - 6 PM (0-23 = 24 slots = 6 hours * 4 slots/hour)
 * Evening: 6 PM - 12 AM (0-23 = 24 slots = 6 hours * 4 slots/hour)
 *
 * @param {number} slotIndex - Slot index within the period (0-23)
 * @param {string} period - Period name ('morning', 'noon', 'evening')
 * @returns {{start: string, end: string}} Start and end times
 */
export const calculateSlotTime = (slotIndex, period) => {
  const baseHours = {
    morning: 6,  // 6 AM
    noon: 12,    // 12 PM
    evening: 18  // 6 PM
  };

  const startHour = baseHours[period];
  const startMinutes = (slotIndex % 4) * 15; // 4 slots per hour (15 min each)
  const hourOffset = Math.floor(slotIndex / 4);

  const start = new Date();
  start.setHours(startHour + hourOffset, startMinutes, 0, 0);

  const end = new Date(start);
  end.setMinutes(end.getMinutes() + 15);

  return {
    start: start.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }),
    end: end.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }),
  };
};

/**
 * Format slot time for display from Date strings
 * @param {string} startTime - Start time ISO string
 * @param {string} endTime - End time ISO string
 * @returns {string} Formatted time range
 */
export const formatSlotTime = (startTime, endTime) => {
  const start = new Date(startTime);
  const end = new Date(endTime);

  // Backend stores times in IST (Asia/Kolkata), display in IST
  const startStr = start.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: 'Asia/Kolkata'
  });

  const endStr = end.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: 'Asia/Kolkata'
  });

  return `${startStr} - ${endStr}`;
};

/**
 * Group slots by period
 * @param {Array} slots - Array of slot objects
 * @returns {{morning: Array, noon: Array, evening: Array}} Grouped slots
 */
export const groupSlotsByPeriod = (slots) => {
  if (!slots || !Array.isArray(slots)) {
    return { morning: [], noon: [], evening: [] };
  }

  return {
    morning: slots.filter(s => s.period === 'morning'),
    noon: slots.filter(s => s.period === 'noon'),
    evening: slots.filter(s => s.period === 'evening'),
  };
};

/**
 * Get period label for display
 * @param {string} period - Period name
 * @returns {string} Display label
 */
export const getPeriodLabel = (period) => {
  const labels = {
    morning: 'Morning (6 AM - 12 PM)',
    noon: 'Afternoon (12 PM - 6 PM)',
    evening: 'Evening (6 PM - 12 AM)',
  };
  return labels[period] || period;
};

/**
 * Get all slot indices for a period (0-23 for each period)
 * @param {string} period - Period name
 * @returns {Array<number>} Array of slot indices
 */
export const getAllPeriodIndices = (period) => {
  // Each period has 24 slots (6 hours * 4 slots per hour)
  return Array.from({ length: 24 }, (_, i) => i);
};

/**
 * Calculate slot duration in minutes
 * @returns {number} Duration in minutes
 */
export const getSlotDuration = () => {
  return 15; // 15 minutes per slot
};

/**
 * Get total number of slots for a period
 * @returns {number} Total slots per period
 */
export const getSlotsPerPeriod = () => {
  return 24; // 6 hours * 4 slots/hour
};

/**
 * Convert slot index and period to a unique identifier
 * @param {string} period - Period name
 * @param {number} slotIndex - Slot index within period
 * @returns {string} Unique slot identifier
 */
export const getSlotId = (period, slotIndex) => {
  return `${period}-${slotIndex}`;
};

/**
 * Parse slot identifier back to period and index
 * @param {string} slotId - Slot identifier
 * @returns {{period: string, slotIndex: number}} Period and index
 */
export const parseSlotId = (slotId) => {
  const [period, index] = slotId.split('-');
  return { period, slotIndex: parseInt(index, 10) };
};
